import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail, mediaUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Wand2, ImagePlay, Repeat, Upload, Sparkles, Download, Loader2, Film, Crown } from "lucide-react";

const MODES = [
  { id: "text", label: "Text to Video", icon: Wand2 },
  { id: "image", label: "Image to Video", icon: ImagePlay },
  { id: "remix", label: "Remix", icon: Repeat },
];
const STYLES = ["Anime", "Cyberpunk", "Film noir", "Vaporwave", "Claymation", "Watercolor", "Pixar 3D", "Retro VHS"];
const ASPECTS = [["16:9", "Landscape"], ["9:16", "Vertical"], ["1:1", "Square"]];

const STAGES = ["Interpreting prompt", "Composing frames", "Rendering motion", "Finishing up"];

export default function Studio() {
  const [mode, setMode] = useState("text");
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("16:9");
  const [duration, setDuration] = useState("5");
  const [style, setStyle] = useState("Cyberpunk");
  const [imageData, setImageData] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(0);
  const [usage, setUsage] = useState(null);
  const fileRef = useRef();
  const pollRef = useRef();
  const nav = useNavigate();

  const needsImage = mode === "image" || mode === "remix";
  const limitReached = usage && !usage.unlimited && usage.remaining <= 0;

  const fetchUsage = useCallback(() => {
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);
  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  useEffect(() => () => clearInterval(pollRef.current), []);
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => setStage((s) => (s + 1) % STAGES.length), 3500);
    return () => clearInterval(t);
  }, [busy]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Image must be under 4MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setImageData(reader.result);
    reader.readAsDataURL(f);
  };

  const poll = useCallback((id) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/jobs/${id}`);
        setJob(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          setBusy(false);
          fetchUsage();
          if (data.status === "failed") toast.error(data.error || "Generation failed");
          else toast.success("Your video is ready!");
        }
      } catch (e) { /* keep polling */ }
    }, 3000);
  }, [fetchUsage]);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe what you want to create"); return; }
    if (needsImage && !imageData) { toast.error("Upload an image first"); return; }
    setBusy(true); setJob(null); setStage(0);
    try {
      const { data } = await api.post("/generate", {
        mode, prompt, aspect_ratio: aspect, duration,
        image_url: needsImage ? imageData : null,
        style: mode === "remix" ? style : null,
      });
      setJob(data);
      fetchUsage();
      poll(data.id);
    } catch (e) {
      setBusy(false);
      fetchUsage();
      if (e.response?.status === 403) {
        toast.error("You've used your 2 free videos. Upgrade to Creator for unlimited.");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 py-10 grain">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Creation Studio</h1>
          <p className="text-muted-foreground mt-1">Pick a mode, shape your idea, and generate motion.</p>
        </div>
        {usage && (
          usage.unlimited ? (
            <span data-testid="usage-badge" className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-accent/15 text-accent">
              <Crown className="w-3.5 h-3.5" /> Unlimited
            </span>
          ) : (
            <div data-testid="usage-badge" className="glass rounded-2xl px-4 py-2.5 text-sm">
              <span className="font-semibold text-primary">{usage.remaining}</span>
              <span className="text-muted-foreground"> of {usage.limit} free videos left</span>
            </div>
          )
        )}
      </div>

      {limitReached && (
        <div data-testid="limit-banner" className="glass rounded-2xl p-5 mb-6 flex flex-wrap items-center justify-between gap-4 border border-accent/30">
          <p className="text-sm">You've used your <span className="font-semibold">2 free videos</span>. Upgrade to Creator for unlimited generations and a saved gallery.</p>
          <Button data-testid="limit-upgrade" onClick={() => nav("/pricing")} className="rounded-full font-semibold">
            <Crown className="w-4 h-4 mr-2" /> Upgrade
          </Button>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Controls */}
        <div className="glass rounded-3xl p-6 sm:p-7 h-fit">
          <div className="grid grid-cols-3 gap-2 mb-6">
            {MODES.map((m) => (
              <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-2xl border text-xs font-semibold transition-colors ${
                  mode === m.id ? "border-primary/60 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"
                }`}>
                <m.icon className="w-5 h-5" />{m.label}
              </button>
            ))}
          </div>

          {needsImage && (
            <div className="mb-5">
              <Label className="text-xs mb-2 block">Source image</Label>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="image-input" />
              <button data-testid="upload-btn" onClick={() => fileRef.current?.click()}
                className="w-full rounded-2xl border border-dashed border-border h-40 grid place-items-center overflow-hidden hover:border-primary/50 transition-colors">
                {imageData ? (
                  <img src={imageData} alt="upload" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-muted-foreground">
                    <Upload className="w-6 h-6 mx-auto mb-2" />
                    <span className="text-sm">Click to upload (max 4MB)</span>
                  </div>
                )}
              </button>
            </div>
          )}

          <div className="mb-5">
            <Label className="text-xs mb-2 block">{mode === "remix" ? "Describe the remix" : "Prompt"}</Label>
            <Textarea data-testid="prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder={mode === "text" ? "A neon koi fish swimming through a rainy Tokyo alley at night, cinematic…" : "Describe the motion and mood you want…"}
              className="rounded-2xl bg-secondary/40 min-h-28 resize-none" />
          </div>

          {mode === "remix" && (
            <div className="mb-5">
              <Label className="text-xs mb-2 block">Style</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger data-testid="style-select" className="rounded-xl bg-secondary/40"><SelectValue /></SelectTrigger>
                <SelectContent>{STYLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-6">
            <div>
              <Label className="text-xs mb-2 block">Aspect ratio</Label>
              <Select value={aspect} onValueChange={setAspect}>
                <SelectTrigger data-testid="aspect-select" className="rounded-xl bg-secondary/40"><SelectValue /></SelectTrigger>
                <SelectContent>{ASPECTS.map(([v, l]) => <SelectItem key={v} value={v}>{l} · {v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-2 block">Duration</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger data-testid="duration-select" className="rounded-xl bg-secondary/40"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="5">5 seconds</SelectItem><SelectItem value="10">10 seconds</SelectItem></SelectContent>
              </Select>
            </div>
          </div>

          {limitReached ? (
            <Button data-testid="generate-btn" onClick={() => nav("/pricing")} className="w-full h-12 rounded-2xl font-semibold">
              <Crown className="w-4 h-4 mr-2" /> Upgrade to keep creating
            </Button>
          ) : (
            <Button data-testid="generate-btn" onClick={generate} disabled={busy} className="w-full h-12 rounded-2xl font-semibold glow-cyan">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate video</>}
            </Button>
          )}
          <p className="text-xs text-muted-foreground text-center mt-3">Rendering can take 1–3 minutes.</p>
        </div>

        {/* Result */}
        <div className="glass rounded-3xl p-6 sm:p-7 min-h-[420px] flex flex-col">
          <Label className="text-xs mb-3 block text-muted-foreground">Output</Label>
          <div className="flex-1 rounded-2xl bg-black/40 border border-border grid place-items-center overflow-hidden">
            <AnimatePresence mode="wait">
              {job?.status === "completed" && job.video_url ? (
                <motion.video key="vid" data-testid="result-video" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  src={mediaUrl(job.video_url)} controls autoPlay loop className="w-full h-full object-contain" />
              ) : busy || job?.status === "processing" || job?.status === "queued" ? (
                <motion.div key="load" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center px-6">
                  <div className="w-16 h-16 rounded-full border-2 border-primary/30 border-t-primary animate-spin mx-auto mb-5" />
                  <p data-testid="gen-stage" className="font-display font-semibold">{STAGES[stage]}…</p>
                  <p className="text-muted-foreground text-sm mt-1">Crafting your {duration}s clip</p>
                </motion.div>
              ) : job?.status === "failed" ? (
                <motion.div key="fail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center px-6">
                  <Film className="w-10 h-10 mx-auto mb-3 text-destructive" />
                  <p data-testid="gen-error" className="text-destructive font-medium">{job.error || "Generation failed"}</p>
                </motion.div>
              ) : (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center px-6 text-muted-foreground">
                  <Film className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Your generated video will appear here.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {job?.status === "completed" && job.video_url && (
            <a data-testid="download-link" href={mediaUrl(job.video_url)} target="_blank" rel="noreferrer" download className="mt-4">
              <Button variant="outline" className="w-full rounded-2xl border-border font-semibold">
                <Download className="w-4 h-4 mr-2" /> Download video
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
